import React from 'react'

import { Box, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

export interface DividerProps {
  /** Escape hatch for the rare local override — the point of this component
   *  is that callers normally don't need one. */
  sx?: SxProps<Theme>
}

/**
 * The ONE divider used across every drawer (+ the Notifications page section
 * break) — 1px, `grey[300]` (the same border color `DrawerPopup`'s own
 * header/footer rules and `Input` already use), with 24px of top AND bottom
 * margin baked in.
 *
 * Before this (VERA-291 follow-up review), the same "divider" had drifted
 * into three different shapes: a bare `borderTop` on `EditFieldModal`'s
 * footer with no margin (sat flush against the field above it), a thick 4px
 * `grey[50]` bar in `UpdatePhoto`, and a second, separately hand-rolled thick
 * 4px `grey[50]` bar on the Notifications page. Consolidating into one
 * component (working-conventions §36 — extract on the second hand-roll) means
 * the thickness/colour/margin only need to be right in one place.
 *
 * A caller just renders `<Divider />` — it never needs to remember to add
 * spacing around it, which is exactly the bug this replaces ("always give
 * the divider on drawers some top margin").
 */
export const Divider: React.FC<DividerProps> = ({ sx }) => {
  const theme = useTheme()

  return (
    <Box
      component='hr'
      sx={{
        border: 'none',
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        m: 0,
        mt: '24px',
        mb: '24px',
        ...sx,
      }}
    />
  )
}
